/*
 * PaywallCinematic — modal fullscreen bloqueante estilo CRT com 3 tiers Cakto.
 * Substitui o PaywallModal genérico quando créditos esgotam (HTTP 402).
 *
 * Variantes:
 *   'trial-ended'        — 1 crédito trial gasto, convida a assinar
 *   'credits-exhausted'  — plano pago sem saldo, convida a renovar/upgrade
 *
 * URLs Cakto (BRL, não mudar):
 *   Plantonista  R$ 99   https://pay.cakto.com.br/wmw5azs_868168
 *   Especialista R$ 175  https://pay.cakto.com.br/6y6b3f8_868238
 *   Unlimited    R$ 399  https://pay.cakto.com.br/9c27rk2_868247
 */

import React, { useEffect, useRef, useState } from 'react';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type PaywallVariant = 'trial-ended' | 'credits-exhausted';

export interface PaywallCinematicProps {
  /** Função de tradução injetada pelo ChatView */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Fecha o modal — chat permanece bloqueado para novas mensagens */
  onClose: () => void;
  /** Determina o header e subtítulo exibidos */
  variant?: PaywallVariant;
  /**
   * Trial v2 (aditivo, opcional): framing de investimento exibido sob o
   * subtítulo — ex. "Nesses 4 dias você correu {n} casos comigo...".
   * undefined = layout atual exato.
   */
  investmentNote?: string;
}

// ─── Dados dos tiers ───────────────────────────────────────────────────────────

interface TierData {
  /** Chave i18n do eyebrow ex: "[ PLANTONISTA ]" */
  eyebrowKey: string;
  /** Chave i18n do preço ex: "R$ 99" */
  priceKey: string;
  /** Chave i18n do período ex: "/mês" */
  periodKey: string;
  /** Chaves i18n das 4 features */
  featKeys: [string, string, string, string];
  /** Chave i18n do botão CTA */
  ctaKey: string;
  /** URL Cakto de checkout */
  url: string;
  /** Se é o card destacado (Especialista) */
  destaque?: boolean;
  /** Chave i18n do badge "★ MAIS POPULAR" — só no card destaque */
  popularKey?: string;
}

const TIERS: TierData[] = [
  {
    eyebrowKey: 'paywall.tiers.plantonista.eyebrow',
    priceKey: 'paywall.tiers.plantonista.price',
    periodKey: 'paywall.tiers.plantonista.period',
    featKeys: [
      'paywall.tiers.plantonista.feat1',
      'paywall.tiers.plantonista.feat2',
      'paywall.tiers.plantonista.feat3',
      'paywall.tiers.plantonista.feat4',
    ],
    ctaKey: 'paywall.tiers.plantonista.cta',
    url: 'https://pay.cakto.com.br/wmw5azs_868168',
  },
  {
    eyebrowKey: 'paywall.tiers.especialista.eyebrow',
    priceKey: 'paywall.tiers.especialista.price',
    periodKey: 'paywall.tiers.especialista.period',
    featKeys: [
      'paywall.tiers.especialista.feat1',
      'paywall.tiers.especialista.feat2',
      'paywall.tiers.especialista.feat3',
      'paywall.tiers.especialista.feat4',
    ],
    ctaKey: 'paywall.tiers.especialista.cta',
    url: 'https://pay.cakto.com.br/6y6b3f8_868238',
    destaque: true,
    popularKey: 'paywall.tiers.especialista.popular',
  },
  {
    eyebrowKey: 'paywall.tiers.unlimited.eyebrow',
    priceKey: 'paywall.tiers.unlimited.price',
    periodKey: 'paywall.tiers.unlimited.period',
    featKeys: [
      'paywall.tiers.unlimited.feat1',
      'paywall.tiers.unlimited.feat2',
      'paywall.tiers.unlimited.feat3',
      'paywall.tiers.unlimited.feat4',
    ],
    ctaKey: 'paywall.tiers.unlimited.cta',
    url: 'https://pay.cakto.com.br/9c27rk2_868247',
  },
];

// ─── Hook de foco/trap ─────────────────────────────────────────────────────────

/**
 * Trap de foco acessível — mantém Tab/Shift+Tab dentro do modal.
 * ESC chama onClose sem desbloquear o chat.
 */
function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  onEsc: () => void,
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Foca o primeiro elemento interativo do modal ao montar
    const focarPrimeiro = () => {
      const focusavel = el.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      focusavel[0]?.focus();
    };
    // RAF garante que o DOM já renderizou
    const raf = requestAnimationFrame(focarPrimeiro);

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onEsc();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusavel = Array.from(
        el.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusavel.length === 0) return;

      const primeiro = focusavel[0];
      const ultimo = focusavel[focusavel.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === primeiro) {
          e.preventDefault();
          ultimo.focus();
        }
      } else {
        if (document.activeElement === ultimo) {
          e.preventDefault();
          primeiro.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [ref, onEsc]);
}

// ─── Sub-componente: card de tier ─────────────────────────────────────────────

interface TierCardProps {
  tier: TierData;
  t: PaywallCinematicProps['t'];
  /** Índice 0-2 para stagger de animação */
  index: number;
  /** Se animação de entrada deve ser suprimida (prefers-reduced-motion) */
  reducedMotion: boolean;
}

function TierCard({ tier, t, index, reducedMotion }: TierCardProps) {
  const [hovered, setHovered] = useState(false);

  // Delay de stagger: 300ms base + 120ms por card
  const entradaDelay = reducedMotion ? 0 : 300 + index * 120;

  // Resting border: violet accent; destaque gets slightly stronger violet
  const borderColor = hovered
    ? 'rgba(124,124,248,0.85)'
    : tier.destaque
      ? 'var(--violet)'
      : 'rgba(124,124,248,0.30)';

  // Resting box-shadow: none for regular, subtle violet glow for destaque
  const boxShadow = hovered
    ? '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)'
    : tier.destaque
      ? '0 0 18px rgba(124,124,248,0.22)'
      : 'none';

  const borderWidth = tier.destaque ? '2px' : '1px';

  // Animação de slide-up + fade (CSS keyframe via style)
  const animStyle: React.CSSProperties = reducedMotion
    ? { opacity: 1 }
    : {
        animation: `paywall-card-in 420ms ease-out ${entradaDelay}ms both`,
      };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={{
        position: 'relative',
        border: `${borderWidth} solid ${borderColor}`,
        boxShadow,
        background: 'var(--ink-soft)',
        padding: '28px 24px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        transition: reducedMotion
          ? 'none'
          : 'border-color 220ms ease-out, box-shadow 220ms ease-out, transform 220ms ease-out',
        transform: hovered && !reducedMotion ? 'scale(1.02)' : 'scale(1)',
        ...animStyle,
      }}
    >
      {/* Badge "★ MAIS POPULAR" — só no card Especialista */}
      {tier.destaque && tier.popularKey && (
        <div
          aria-label={t(tier.popularKey)}
          style={{
            position: 'absolute',
            top: '-14px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--violet)',
            color: '#FFFFFF',
            fontFamily: 'var(--font-sans)',
            fontSize: '11px',
            fontWeight: 700,
            padding: '4px 12px',
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            boxShadow: '0 0 10px rgba(124,124,248,0.45)',
          }}
        >
          {t(tier.popularKey)}
        </div>
      )}

      {/* Eyebrow — ex: "[ PLANTONISTA ]" */}
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '14px',
          fontWeight: 700,
          color: '#FFFFFF',
          textShadow: 'none',
          marginBottom: '10px',
          letterSpacing: '0.04em',
        }}
      >
        {t(tier.eyebrowKey)}
      </div>

      {/* Bloco de preço */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '4px',
          marginBottom: '18px',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '26px',
            fontWeight: 700,
            color: '#FFFFFF',
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {t(tier.priceKey)}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '14px',
            color: 'var(--ink-mute)',
          }}
        >
          {t(tier.periodKey)}
        </span>
      </div>

      {/* Lista de features */}
      <ul
        style={{
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginBottom: '22px',
          flex: 1,
        }}
      >
        {tier.featKeys.map((key) => (
          <li
            key={key}
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '13px',
              color: 'var(--ink-light)',
              lineHeight: 1.5,
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-start',
            }}
          >
            <span
              style={{
                color: 'var(--violet)',
                flexShrink: 0,
                fontSize: '11px',
                marginTop: '2px',
              }}
            >
              ▶
            </span>
            {t(key)}
          </li>
        ))}
      </ul>

      {/* Botão CTA — cta-blue preenchido em repouso, hover com glow laranja */}
      <a
        href={tier.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          textAlign: 'center',
          padding: '12px 16px',
          background: hovered ? '#0050d0' : 'var(--cta-blue)',
          border: `1px solid ${hovered ? '#FF6A00' : 'var(--cta-blue)'}`,
          color: '#FFFFFF',
          fontFamily: 'var(--font-sans)',
          fontSize: '12px',
          fontWeight: 600,
          textDecoration: 'none',
          letterSpacing: '0.04em',
          boxShadow: hovered
            ? '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)'
            : 'none',
          transition: reducedMotion
            ? 'none'
            : 'background 220ms, border-color 220ms, box-shadow 220ms',
          cursor: 'pointer',
        }}
      >
        {t(tier.ctaKey)}
      </a>
    </div>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────────

export function PaywallCinematic({
  t,
  onClose,
  variant = 'trial-ended',
  investmentNote,
}: PaywallCinematicProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [visivel, setVisivel] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Detecta prefers-reduced-motion uma vez ao montar
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
  }, []);

  // Trigger de entrada — pequeno delay para o backdrop ter tempo de montar
  useEffect(() => {
    const id = setTimeout(() => setVisivel(true), 16);
    return () => clearTimeout(id);
  }, []);

  // Trap de foco + ESC fecha (mas chat permanece bloqueado)
  useFocusTrap(modalRef, onClose);

  // Chaves de header/subtítulo baseadas na variante
  const titleKey =
    variant === 'trial-ended'
      ? 'paywall.title.trial'
      : 'paywall.title.exhausted';
  const subtitleKey =
    variant === 'trial-ended'
      ? 'paywall.sub.trial'
      : 'paywall.sub.exhausted';

  // Duração de fade do backdrop
  const backdropOpacity = visivel || reducedMotion ? '0.93' : '0';
  const backdropTransition = reducedMotion ? 'none' : 'opacity 200ms ease-out';

  return (
    <>
      {/* Keyframes injetados via <style> — sem lib externa */}
      <style>{`
        @keyframes paywall-card-in {
          from { opacity: 0; transform: translateY(18px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes paywall-title-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes paywall-sub-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes paywall-question-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes paywall-card-in    { from { opacity: 1; } to { opacity: 1; } }
          @keyframes paywall-title-in   { from { opacity: 1; } to { opacity: 1; } }
          @keyframes paywall-sub-in     { from { opacity: 1; } to { opacity: 1; } }
          @keyframes paywall-question-in { from { opacity: 1; } to { opacity: 1; } }
        }
      `}</style>

      {/* Backdrop */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t(titleKey)}
        ref={modalRef}
        style={{
          position: 'fixed',
          inset: 0,
          background: `rgba(34,37,50,${backdropOpacity})`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          zIndex: 600,
          overflowY: 'auto',
          padding: '40px 16px 48px',
          transition: backdropTransition,
        }}
      >
        {/* ── Conteúdo principal ── */}
        <div
          style={{
            width: '100%',
            maxWidth: '1080px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0',
          }}
        >
          {/* Header — title white, slide-in animation */}
          <h1
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(16px, 3.5vw, 28px)',
              fontWeight: 700,
              color: '#FFFFFF',
              textAlign: 'center',
              letterSpacing: '0.04em',
              lineHeight: 1.3,
              marginBottom: '14px',
              textShadow: 'none',
              animation: reducedMotion
                ? 'none'
                : 'paywall-title-in 500ms ease-out 100ms both',
            }}
          >
            {t(titleKey)}
          </h1>

          {/* Subtítulo — violet accent */}
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(14px, 2.5vw, 18px)',
              color: 'var(--violet)',
              textShadow: 'none',
              textAlign: 'center',
              marginBottom: '8px',
              animation: reducedMotion
                ? 'none'
                : 'paywall-sub-in 400ms ease-out 250ms both',
            }}
          >
            {t(subtitleKey)}
          </p>

          {/* Trial v2 — framing de investimento ("já correste N casos comigo") */}
          {investmentNote && (
            <p
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'clamp(13px, 2vw, 15px)',
                color: 'var(--ink-light)',
                textAlign: 'center',
                lineHeight: 1.6,
                maxWidth: '640px',
                marginBottom: '8px',
                animation: reducedMotion
                  ? 'none'
                  : 'paywall-sub-in 400ms ease-out 350ms both',
              }}
            >
              {investmentNote}
            </p>
          )}

          {/* Pergunta "Que tal continuar?" */}
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(12px, 1.5vw, 14px)',
              color: 'var(--ink-mute)',
              textAlign: 'center',
              letterSpacing: '0.05em',
              marginBottom: '40px',
              animation: reducedMotion
                ? 'none'
                : 'paywall-question-in 350ms ease-out 600ms both',
            }}
          >
            {t('paywall.cta.question')}
          </p>

          {/* Grid de 3 cards de tier — responsivo via classe CSS */}
          <div className="paywall-cinematic-grid">
            {TIERS.map((tier, idx) => (
              <TierCard
                key={tier.eyebrowKey}
                tier={tier}
                t={t}
                index={idx}
                reducedMotion={reducedMotion}
              />
            ))}
          </div>

          {/* Nota BRL — cobrança em reais, banco converte */}
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '12px',
              color: 'var(--ink-mute)',
              textAlign: 'center',
              marginBottom: '32px',
              lineHeight: 1.5,
            }}
          >
            {t('paywall.brlNote')}
          </p>

          {/* Footer com 2 botões menores */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <a
              href="/login"
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                color: 'var(--ink-light)',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                cursor: 'pointer',
              }}
            >
              {t('paywall.footer.alreadySubscriber')}
            </a>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                color: 'var(--ink-mute)',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
            >
              {t('paywall.footer.backToChat')}
            </button>
          </div>
        </div>
      </div>

      {/* CSS responsivo para mobile — cards em 1 coluna */}
      <style>{`
        .paywall-cinematic-grid {
          width: 100%;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 16px;
        }
        @media (max-width: 860px) {
          .paywall-cinematic-grid {
            grid-template-columns: 1fr;
            max-width: 420px;
          }
        }
      `}</style>
    </>
  );
}
